import React from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Brain, Layers, Image as ImageIcon, Database, BookOpen } from 'lucide-react';
import { Badge } from '../../../../../components/ui/Badge';
import { IssueRow, MODULE_CONFIG } from '../shared';
import type { UnifiedQuestionRow } from '../hooks/useGovernanceData';
interface QuestionDetailPanelProps {
  row: UnifiedQuestionRow;
}
export function QuestionDetailPanel({
  row
}: QuestionDetailPanelProps) {
  const modConfig = MODULE_CONFIG[row.moduleCode] || {
    bgClass: 'bg-gray-500',
    textClass: 'text-gray-700'
  };
  return <div className="px-4 py-4 bg-gray-50 border-t border-gray-100" data-id="element-5314">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5315">
        {/* Left Column: Pipeline Issues */}
        <div className="space-y-4" data-id="element-5316">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5" data-id="element-5317">
            <AlertTriangle className="w-3.5 h-3.5" data-id="element-5318" /> Pipeline Issues
          </h4>

          {row.pipelineIssues.length === 0 ? <div className="p-4 bg-white rounded-lg border border-gray-200 text-center" data-id="element-5319">
              <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-1" data-id="element-5320" />
              <p className="text-sm text-gray-600" data-id="element-5321">
                Geen pipeline issues gevonden.
              </p>
            </div> : <div className="space-y-2" data-id="element-5322">
              {row.pipelineIssues.map((issue, idx) => <IssueRow key={idx} issue={issue} data-id="element-5323" />)}
            </div>}
        </div>

        {/* Right Column: Metadata & Quality */}
        <div className="space-y-6" data-id="element-5324">
          {/* Quality & Metadata */}
          <div data-id="element-5325">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5326">
              <Brain className="w-3.5 h-3.5" data-id="element-5327" /> Kwaliteit & Metadata
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4" data-id="element-5328">
              <div className="grid grid-cols-2 gap-4" data-id="element-5329">
                <div data-id="element-5330">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5331">Cognitief Niveau</p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-5332">
                    Niveau {row.cognitiveLevel}
                  </p>
                </div>
                <div data-id="element-5333">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5334">Module & Tier</p>
                  <div className="flex items-center gap-1.5" data-id="element-5335">
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${modConfig.bgClass} text-white`} data-id="element-5336">
                      {row.moduleCode}
                    </span>
                    <span className="text-sm font-medium text-gray-900" data-id="element-5337">
                      Tier {row.tier}
                    </span>
                  </div>
                </div>
                <div data-id="element-5338">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5339">Vraagtype</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-5340">
                    {row.questionType}
                  </p>
                </div>
                <div data-id="element-5341">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5342">Skill</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-5343">
                    {row.skill}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Seed Status */}
          <div data-id="element-5344">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5345">
              <Database className="w-3.5 h-3.5" data-id="element-5346" /> Seed Status
            </h4>

            {row.seedWarnings.length === 0 ? <div className="p-3 bg-white rounded-lg border border-gray-200 flex items-center gap-2" data-id="element-5347">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-5348" />
                <span className="text-sm text-gray-600" data-id="element-5349">
                  In sync met taxonomy
                </span>
              </div> : <div className="space-y-2" data-id="element-5350">
                {row.seedWarnings.map((warn, i) => <div key={i} className="p-2.5 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-800 font-mono" data-id="element-5351">
                    {warn}
                  </div>)}
              </div>}
          </div>

          {/* Visual Status */}
          <div data-id="element-5352">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5353">
              <ImageIcon className="w-3.5 h-3.5" data-id="element-5354" /> Visuele Hulpmiddelen
            </h4>

            <div className="p-3 bg-white rounded-lg border border-gray-200" data-id="element-5355">
              {row.hasVisualProblems ? <div className="flex items-start gap-2 text-red-700" data-id="element-5356">
                  <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-5357" />
                  <span className="text-sm" data-id="element-5358">
                    Er zijn problemen met de visuals (zie pipeline issues).
                  </span>
                </div> : <div className="flex items-start gap-2 text-emerald-700" data-id="element-5359">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-5360" />
                  <span className="text-sm" data-id="element-5361">
                    Visuals zijn geldig en correct geconfigureerd.
                  </span>
                </div>}
            </div>
          </div>

          {/* Pedagogisch Contract */}
          <div data-id="element-5362">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5363">
              <BookOpen className="w-3.5 h-3.5" data-id="element-5364" /> Pedagogisch Contract
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3" data-id="element-5365">
              {(() => {
              const pedagogyIssues = row.pipelineIssues.filter(i => i.domain === 'pedagogy');
              const hasPedagogyBlockers = pedagogyIssues.some(i => i.kind === 'blocker');
              const hasPedagogyCoaches = pedagogyIssues.some(i => i.kind === 'coach');
              return <>
                    <div className="grid grid-cols-2 gap-3" data-id="element-5366">
                      <div data-id="element-5367">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-5368">DOK Niveau</p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-5369">
                          {row.pipelineIssues.some(i => i.code === 'SEED_DOK_MISSING') ? <span className="text-amber-600" data-id="element-5370">
                              Niet ingesteld
                            </span> : 'Ingesteld'}
                        </p>
                      </div>
                      <div data-id="element-5371">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-5372">
                          Misconceptie Tags
                        </p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-5373">
                          {pedagogyIssues.some(i => i.code === 'PEDAGOGY_DISTRACTORS_UNTAGGED') ? <span className="text-amber-600" data-id="element-5374">Ontbreekt</span> : pedagogyIssues.some(i => i.code === 'PEDAGOGY_DISTRACTORS_PARTIAL') ? <span className="text-amber-500" data-id="element-5375">Gedeeltelijk</span> : <span className="text-emerald-600" data-id="element-5376">Compleet</span>}
                        </p>
                      </div>
                    </div>
                    {pedagogyIssues.length === 0 ? <div className="flex items-center gap-2 pt-1" data-id="element-5377">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-5378" />
                        <span className="text-sm text-gray-600" data-id="element-5379">
                          Pedagogisch contract voldoet aan alle eisen.
                        </span>
                      </div> : <div className="flex items-center gap-2 pt-1" data-id="element-5380">
                        {hasPedagogyBlockers ? <XCircle className="w-4 h-4 text-red-500" data-id="element-5381" /> : <AlertTriangle className="w-4 h-4 text-amber-500" data-id="element-5382" />}
                        <span className="text-sm text-gray-600" data-id="element-5383">
                          {pedagogyIssues.length} pedagogische{' '}
                          {pedagogyIssues.length === 1 ? 'bevinding' : 'bevindingen'}
                          {hasPedagogyBlockers ? ' (blocker)' : hasPedagogyCoaches ? ' (verbeterpunt)' : ''}
                        </span>
                      </div>}
                  </>;
            })()}
            </div>
          </div>
        </div>
      </div>
    </div>;
}